import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../context/LanguageContext";
import { projectsService, Project } from "../services/projects";
import ScenarioBuilder, { ScenarioConfig } from "../components/fts/ScenarioBuilder";
import WeatherConditionsPanel from "../components/fts/WeatherConditionsPanel";
import ConopsMapEditor from "../components/fts/ConopsMapEditor";
import { Play, ArrowLeft, FloppyDisk, CircleNotch } from "@phosphor-icons/react";
import { MapTrifold, CaretDown, CaretUp, Info } from "@phosphor-icons/react";
import type { ConopsGeoJSON } from "../types/conops";

const DEFAULT_CONFIG: ScenarioConfig = {
  name: "",
  description: "",
  project_id: "",
  is_baseline: false,
  ams_enabled: false,
  ams_config: {
    handover_latency_reduction: 0.5,
    smart_sequencing_mode: null,
    simultaneous_pushback: false,
    contiguous_pushback: false,
    simultaneous_contiguous_pushback: false,
    dynamic_routing: false,
    adaptive_routing: false,
    safety_blocking: false,
  },
  fastgate_enabled: false,
  fastgate_config: {
    pushback_reduction: 0.70,
    eligible_types: ["A320", "A321", "B737", "B738"],
  },
  turnaround_monitoring_enabled: false,
  turnaround_monitoring_config: {
    provider: "assaia" as const,
    turnaround_reduction: 0.15,
  },
  infra_changes_enabled: false,
  infra_changes_config: {
    infrastructure_types: [],
    new_taxiways: [],
    new_stands: [],
    tcf_reduction: 0,
    what_improves: "",
    how_improves: "",
    magnitude: "",
  },
  standard_routes_enabled: false,
  standard_routes_config: {
    routes_description: "",
  },
  dacams_improvements_enabled: false,
};

const SimulationCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { language } = useLanguage();

  const location = useLocation();
  const editSimulation = (location.state as any)?.editSimulation || null;
  const isEditing = !!editSimulation;

  const [config, setConfig] = useState<ScenarioConfig>(() => {
    if (editSimulation) {
      const oldSop = editSimulation.new_sop_config || {};
      return {
        ...DEFAULT_CONFIG,
        name: editSimulation.name || '',
        description: editSimulation.description || '',
        project_id: editSimulation.project_id || '',
        is_baseline: editSimulation.is_baseline || false,
        ams_enabled: editSimulation.ams_enabled || false,
        ams_config: {
          ...DEFAULT_CONFIG.ams_config,
          ...(editSimulation.ams_config || {}),
          // migrate legacy SOP fields into ams_config
          simultaneous_pushback: oldSop.simultaneous_pushback ?? DEFAULT_CONFIG.ams_config.simultaneous_pushback,
          dynamic_routing: oldSop.dynamic_routing ?? DEFAULT_CONFIG.ams_config.dynamic_routing,
          safety_blocking: oldSop.safety_blocking ?? DEFAULT_CONFIG.ams_config.safety_blocking,
        },
        fastgate_enabled: editSimulation.fastgate_enabled || false,
        fastgate_config: editSimulation.fastgate_config || DEFAULT_CONFIG.fastgate_config,
        turnaround_monitoring_enabled: editSimulation.turnaround_monitoring_enabled || false,
        turnaround_monitoring_config: editSimulation.turnaround_monitoring_config || DEFAULT_CONFIG.turnaround_monitoring_config,
        infra_changes_enabled: editSimulation.infra_changes_enabled || false,
        infra_changes_config: {
          ...DEFAULT_CONFIG.infra_changes_config,
          ...(editSimulation.infra_changes_config || {}),
        },
        standard_routes_enabled: editSimulation.standard_routes_enabled || false,
        standard_routes_config: editSimulation.standard_routes_config || DEFAULT_CONFIG.standard_routes_config,
        dacams_improvements_enabled: editSimulation.dacams_improvements_enabled || false,
      };
    }
    return DEFAULT_CONFIG;
  });
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [weatherParams, setWeatherParams] = useState<Record<string, any> | null>(null);
  const [busyDay, setBusyDay] = useState<string | undefined>(undefined);
  const [conopsGeojson, setConopsGeojson] = useState<ConopsGeoJSON | null>(
    editSimulation?.conops_geojson ?? null
  );
  const [showMapEditor, setShowMapEditor] = useState(false);

  // Derive ICAO code from selected project
  const selectedIcao = useMemo(() => {
    if (!config.project_id) return '';
    const proj = projects.find(p => p.id === config.project_id);
    return proj?.icao_code || '';
  }, [config.project_id, projects]);

  const isDark = theme === 'dark';
  const textMain = 'text-[var(--text-primary)]';
  const textSub = 'text-[var(--text-secondary)]';

  useEffect(() => {
    loadProjects();
  }, []);

  // Extract IATA busy day from selected project's analysis_results
  useEffect(() => {
    if (!config.project_id) {
      setBusyDay(undefined);
      return;
    }
    const proj = projects.find(p => p.id === config.project_id);
    if (!proj?.analysis_results) {
      setBusyDay(undefined);
      return;
    }
    // Primary: top-level iata_busy_day (stored directly from APCAS pipeline)
    // Fallback: advanced_analysis.charts.departure_distribution.busy_day
    const day: string | undefined =
      proj.analysis_results?.iata_busy_day ||
      proj.analysis_results?.advanced_analysis?.charts?.departure_distribution?.busy_day ||
      proj.analysis_results?.advanced_analysis?.charts?.bank_analysis?.busy_day;
    setBusyDay(day || undefined);
  }, [config.project_id, projects]);

  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const data = await projectsService.getAll();
      // Only show projects with completed analysis
      setProjects(data.filter(p => p.has_file && p.analysis_status === 'COMPLETED'));
    } catch (error) {
      console.error("Error loading projects:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config.name || !config.project_id) {
      alert(language === 'es'
        ? "Por favor, complete el nombre y seleccione un proyecto."
        : "Please complete the name and select a project.");
      return;
    }

    setIsSaving(true);

    try {
      // Import service
      const { simulationsService } = await import('../services/simulations');

      // Create simulation via API
      const created = await simulationsService.create({
        name: config.name,
        description: config.description,
        project_id: config.project_id,
        is_baseline: config.is_baseline,
        ams_enabled: config.ams_enabled,
        ams_config: {
          handover_latency_reduction: config.ams_config.handover_latency_reduction,
          smart_sequencing_mode: config.ams_config.smart_sequencing_mode,
        },
        fastgate_enabled: config.fastgate_enabled,
        fastgate_config: config.fastgate_config,
        turnaround_monitoring_enabled: config.turnaround_monitoring_enabled,
        turnaround_monitoring_config: config.turnaround_monitoring_config,
        infra_changes_enabled: config.infra_changes_enabled,
        infra_changes_config: config.infra_changes_config,
        new_sop_enabled: config.ams_enabled,
        new_sop_config: {
          simultaneous_pushback: config.ams_config.simultaneous_pushback,
          contiguous_pushback: config.ams_config.contiguous_pushback,
          simultaneous_contiguous_pushback: config.ams_config.simultaneous_contiguous_pushback,
          dynamic_routing: config.ams_config.dynamic_routing,
          adaptive_routing: config.ams_config.adaptive_routing,
          safety_blocking: config.ams_config.safety_blocking,
        },
        standard_routes_enabled: config.standard_routes_enabled,
        standard_routes_config: config.standard_routes_enabled ? config.standard_routes_config : undefined,
        dacams_improvements_enabled: config.dacams_improvements_enabled,
        conops_config: undefined,
        conops_geojson: conopsGeojson ?? undefined,
        simulation_parameters: weatherParams || undefined,
      });

      console.log("Simulation created:", created);
      navigate(`/fts/${created.id}`);
    } catch (error) {
      console.error("Error creating simulation:", error);
      alert(language === 'es'
        ? "Error al crear la simulación. Revise la consola."
        : "Error creating simulation. Check console.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!config.name || !config.project_id) {
      alert(language === 'es'
        ? "Por favor, complete el nombre y seleccione un proyecto."
        : "Please complete the name and select a project.");
      return;
    }

    setIsSaving(true);

    try {
      const { simulationsService } = await import('../services/simulations');

      // Create simulation
      const created = await simulationsService.create({
        name: config.name,
        description: config.description,
        project_id: config.project_id,
        is_baseline: config.is_baseline,
        ams_enabled: config.ams_enabled,
        ams_config: {
          handover_latency_reduction: config.ams_config.handover_latency_reduction,
          smart_sequencing_mode: config.ams_config.smart_sequencing_mode,
        },
        fastgate_enabled: config.fastgate_enabled,
        fastgate_config: config.fastgate_config,
        turnaround_monitoring_enabled: config.turnaround_monitoring_enabled,
        turnaround_monitoring_config: config.turnaround_monitoring_config,
        infra_changes_enabled: config.infra_changes_enabled,
        infra_changes_config: config.infra_changes_config,
        new_sop_enabled: config.ams_enabled,
        new_sop_config: {
          simultaneous_pushback: config.ams_config.simultaneous_pushback,
          contiguous_pushback: config.ams_config.contiguous_pushback,
          simultaneous_contiguous_pushback: config.ams_config.simultaneous_contiguous_pushback,
          dynamic_routing: config.ams_config.dynamic_routing,
          adaptive_routing: config.ams_config.adaptive_routing,
          safety_blocking: config.ams_config.safety_blocking,
        },
        standard_routes_enabled: config.standard_routes_enabled,
        standard_routes_config: config.standard_routes_enabled ? config.standard_routes_config : undefined,
        dacams_improvements_enabled: config.dacams_improvements_enabled,
        conops_config: undefined,
        conops_geojson: conopsGeojson ?? undefined,
        simulation_parameters: weatherParams || undefined,
      });

      // Run simulation immediately
      await simulationsService.run(created.id);

      console.log("Simulation created and running:", created);
      navigate(`/fts/${created.id}`);
    } catch (error) {
      console.error("Error:", error);
      alert(language === 'es'
        ? "Error al ejecutar la simulación."
        : "Error running simulation.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-8 font-sans min-h-full">
      <div className="max-w-4xl mx-auto">

        {/* HEADER */}
        <div className={`flex items-center justify-between mb-8 pb-6 border-b ${isDark ? 'border-[#2a2a2a]' : 'border-gray-300'}`}>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/fts')}
              className={`p-2 rounded-lg hover:bg-gray-500/10 ${textSub}`}
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className={`text-3xl font-extrabold flex items-center gap-3 ${textMain}`}>
                <Play className="w-7 h-7 text-emerald-500" />
                {language === 'es'
                  ? (isEditing ? 'Editar Simulación FTS' : 'Nueva Simulación FTS')
                  : (isEditing ? 'Edit FTS Simulation' : 'New FTS Simulation')}
              </h1>
              <p className={`text-sm mt-1 ${textSub}`}>
                {language === 'es'
                  ? 'Configura un escenario What-If para simular mejoras operativas'
                  : 'Configure a What-If scenario to simulate operational improvements'}
              </p>
            </div>
          </div>
        </div>

        {/* LOADING STATE */}
        {isLoading ? (
          <div className="flex justify-center py-32">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-emerald-500"></div>
          </div>
        ) : projects.length === 0 ? (
          <div className={`flex flex-col items-center justify-center py-24 rounded-2xl border-2 border-dashed ${isDark ? 'border-[var(--border)] bg-[var(--surface-raised)]0' : 'border-gray-300 bg-gray-50'}`}>
            <Play className="h-12 w-12 text-[var(--text-secondary)] mb-4" />
            <p className={textSub}>
              {language === 'es'
                ? 'No hay proyectos DACAMS con análisis completado.'
                : 'No DACAMS projects with completed analysis.'}
            </p>
            <button
              onClick={() => navigate('/home')}
              className={`mt-4 text-sm ${isDark ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'}`}
            >
              {language === 'es' ? '→ Ir al HUB DACAMS' : '→ Go to DACAMS HUB'}
            </button>
          </div>
        ) : (
          <>
            {/* SCENARIO BUILDER */}
            <ScenarioBuilder
              config={config}
              onChange={setConfig}
              projects={projects.map(p => ({ id: p.id, name: p.name, iata_code: p.iata_code }))}
            />

            {/* CONOPS MAP EDITOR — visual AoR / handover / taxi routes */}
            <div className={`mb-6 rounded-xl border overflow-hidden ${isDark ? 'border-[var(--border)] bg-[var(--surface)]' : 'border-gray-200 bg-white'}`}>
              {/* Collapsible header */}
              <button
                onClick={() => setShowMapEditor(!showMapEditor)}
                className={`w-full flex items-center justify-between px-5 py-4 text-left transition-colors ${
                  isDark ? 'hover:bg-[var(--surface-raised)]' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <MapTrifold size={20} className="text-[#F26430]" weight="duotone" />
                  <div>
                    <span className={`font-bold text-sm ${textMain}`}>
                      CONOPS Map Editor (avanzado)
                    </span>
                    <p className={`text-xs mt-0.5 ${textSub}`}>
                      Define visualmente el AoR, puntos de transferencia y rutas de rodaje
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {conopsGeojson && conopsGeojson.features.length > 0 && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#F26430]/20 text-[#F26430]">
                      {conopsGeojson.features.length} elem.
                    </span>
                  )}
                  {showMapEditor ? (
                    <CaretUp size={16} className={textSub} />
                  ) : (
                    <CaretDown size={16} className={textSub} />
                  )}
                </div>
              </button>

              {showMapEditor && (
                <div className="border-t border-[var(--border)]">
                  {/* Info note */}
                  <div className={`flex items-start gap-2 mx-5 my-3 px-3 py-2 rounded-md text-xs ${isDark ? 'bg-blue-500/10 border border-blue-500/20 text-blue-300' : 'bg-blue-50 border border-blue-200 text-blue-700'}`}>
                    <Info size={14} className="flex-shrink-0 mt-0.5" />
                    <span>
                      Define visualmente el AoR, puntos de transferencia (llegada/salida) y rutas de rodaje sobre el mapa del aeropuerto. El GeoJSON generado alimenta directamente el motor FTS.
                    </span>
                  </div>
                  <div className="px-5 pb-5">
                    <ConopsMapEditor
                      icaoCode={selectedIcao}
                      scenarioId={editSimulation?.id}
                      value={conopsGeojson}
                      onChange={setConopsGeojson}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* WEATHER CONDITIONS PANEL */}
            {config.project_id && (
              <WeatherConditionsPanel
                icaoCode={selectedIcao}
                defaultDate={busyDay}
                onParamsChange={(params) => {
                  setWeatherParams(params);
                  setConfig(prev => ({ ...prev, simulation_parameters: params }));
                }}
              />
            )}

            {/* ACTION BUTTONS */}
            <div className={`mt-8 p-6 rounded-xl border flex flex-col sm:flex-row gap-4 justify-between items-center ${isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-[var(--border)]'}`}>
              <button
                onClick={() => navigate('/fts')}
                className={`px-6 py-3 rounded-lg font-medium ${textSub} hover:bg-gray-500/10`}
              >
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold border transition-colors
                      ${isDark ? 'border-[var(--border)] text-white hover:bg-[var(--surface-inset)]' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}
                      disabled:opacity-50`}
                >
                  {isSaving ? <CircleNotch className="animate-spin" size={18} /> : <FloppyDisk size={18} />}
                  {language === 'es' ? 'Guardar Borrador' : 'Save Draft'}
                </button>

                <button
                  onClick={handleRunNow}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-6 py-3 rounded-lg font-bold bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
                >
                  {isSaving ? <CircleNotch className="animate-spin" size={18} /> : <Play size={18} />}
                  {language === 'es' ? 'Ejecutar Simulación' : 'Run Simulation'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SimulationCreatePage;